import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// Cache global pour les requêtes
const globalCache: Record<string, { data: any; timestamp: number }> = {};
// Durée de validité du cache en ms (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

export function useCachedQuery<T>(
  table: string,
  filters: Record<string, any> = {},
  options: { 
    single?: boolean; 
    withRelations?: string;
    cacheDisabled?: boolean;
  } = {}
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Clé de cache basée sur les paramètres de la requête
  const cacheKey = `${table}_${JSON.stringify(filters)}_${options.single}_${options.withRelations}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Vérifier le cache si le cache n'est pas désactivé
        if (!options.cacheDisabled) {
          // Vérifier le cache global
          if (globalCache[cacheKey]) {
            const { data: cachedData, timestamp } = globalCache[cacheKey];
            // Cache valide pendant 5 minutes
            if (Date.now() - timestamp < CACHE_TTL) {
              setData(cachedData as T);
              setError(null);
              setLoading(false);
              return;
            }
          }
          
          // Vérifier le cache local (localStorage)
          try {
            const cachedDataString = localStorage.getItem(`query_${cacheKey}`);
            if (cachedDataString) {
              const { data: cachedData, timestamp } = JSON.parse(cachedDataString);
              // Cache valide pendant 5 minutes
              if (Date.now() - timestamp < CACHE_TTL) {
                setData(cachedData as T);
                setError(null);
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.error('Erreur de lecture du cache:', error);
          }
        }

        // Si pas de données en cache ou cache expiré, faire la requête
        let query = supabase
          .from(table)
          .select(options.withRelations || '*');

        // Appliquer les filtres
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (key === 'orderBy') {
              query = query.order(value);
            } else {
              query = query.eq(key, value);
            }
          }
        });

        let result;
        let queryError;

        if (options.single) {
          try {
            const { data: singleResult, error: singleError } = await query.single();
            result = singleResult;
            queryError = singleError;
          } catch (err) {
            // Si l'erreur est PGRST116 (multiple rows returned), on récupère la première ligne
            if (err && typeof err === 'object' && 'code' in err && err.code === 'PGRST116') {
              console.warn('Multiple rows returned for single() query, using first row');
              const { data: multipleResult, error: multipleError } = await query.limit(1);
              result = multipleResult?.[0] || null;
              queryError = multipleError;
            } else {
              throw err;
            }
          }
        } else {
          const { data: multipleResult, error: multipleError } = await query;
          result = multipleResult;
          queryError = multipleError;
        }

        if (queryError) {
          throw queryError;
        }

        // Stocker dans le cache si le cache n'est pas désactivé
        if (!options.cacheDisabled && result) {
          // Cache en mémoire
          globalCache[cacheKey] = { data: result, timestamp: Date.now() };
          
          // Cache local
          try {
            localStorage.setItem(
              `query_${cacheKey}`,
              JSON.stringify({ data: result, timestamp: Date.now() })
            );
          } catch (error) {
            console.error('Erreur d\'écriture du cache:', error);
          }
        }

        setData(result as T);
        setError(null);
      } catch (err) {
        console.error('Query error:', err);
        const errorMessage = err instanceof Error ? err.message : 'An error occurred';
        setError(errorMessage);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [table, JSON.stringify(filters), options.single, options.withRelations, options.cacheDisabled, cacheKey]);

  // Fonction pour rafraîchir manuellement les données
  const refresh = () => {
    // Supprimer du cache
    delete globalCache[cacheKey];
    try {
      localStorage.removeItem(`query_${cacheKey}`);
    } catch (e) {
      console.error('Erreur lors de la suppression du cache:', e);
    }
    
    // Recharger les données
    setLoading(true);
    // useEffect se déclenchera automatiquement car on a modifié le cache
  };

  return { data, loading, error, refresh };
} 